import { useEffect, useMemo, useState } from "react";
import {
  Share2, Copy, CheckCircle2, MessageCircle, Mail, Users,
  ShoppingBag, TrendingUp, CircleDollarSign, Loader2, QrCode, Download,
} from "lucide-react";
import { myApi } from "../api";

const fmt = (v) => new Intl.NumberFormat("pt-PT", { style: "currency", currency: "EUR" }).format(v || 0);
const fmtNum = (v) => new Intl.NumberFormat("pt-PT").format(v || 0);

function buildShareText(link) {
  return (
    "Olá! Quero partilhar consigo o I HAVE THE POWER — uma comunidade que me " +
    "tem ajudado a crescer pessoal e profissionalmente. " +
    "Acede pelo meu link: " + link
  );
}

export default function MyRecomendar() {
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    setLoadErr("");
    myApi.recomendar()
      .then((d) => { if (active) setData(d); })
      .catch((e) => { if (active) setLoadErr(e?.message || "Erro a carregar"); });
    return () => { active = false; };
  }, []);

  const link = data?.link || "";
  const shareText = useMemo(() => (link ? buildShareText(link) : ""), [link]);

  const waHref = link
    ? `https://wa.me/?text=${encodeURIComponent(shareText)}`
    : "#";
  const mailHref = link
    ? `mailto:?subject=${encodeURIComponent("Acho que vais gostar disto — I HAVE THE POWER")}&body=${encodeURIComponent(shareText)}`
    : "#";
  const qrUrl = link
    ? `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=12&data=${encodeURIComponent(link)}`
    : "";

  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Fallback: select-text in input is automatic on click
    }
  };

  const handleNativeShare = async () => {
    if (!link) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "I HAVE THE POWER",
          text: shareText,
          url: link,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      handleCopy();
    }
  };

  return (
    <div data-testid="my-recomendar-page" className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-4xl text-[#0F1829] mb-1" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
          Recomendar a um amigo
        </h1>
        <p className="text-sm text-[#6B7280]">
          Partilhe o seu link pessoal — cada nova encomenda paga gera-lhe ganhos automaticamente.
        </p>
      </div>

      {loadErr && (
        <div data-testid="my-recomendar-error" className="rounded-[14px] border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {loadErr}
        </div>
      )}

      {/* Hero — link + acções */}
      <div className="bg-gradient-to-br from-[#0F1829] to-[#1a2542] text-white rounded-[20px] p-6 md:p-8" data-testid="my-recomendar-hero">
        <div className="flex items-center gap-2 mb-2">
          <Share2 size={14} className="text-[#D1CFC9]" />
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/60">O seu link pessoal</span>
        </div>
        <div
          className="flex items-stretch gap-2 mt-3 bg-white/10 border border-white/15 rounded-[14px] p-2"
        >
          <input
            data-testid="my-recomendar-link-input"
            readOnly
            value={data ? link : "A carregar..."}
            onFocus={(e) => e.target.select()}
            className="flex-1 bg-transparent outline-none px-3 py-2 text-sm md:text-base font-mono text-white placeholder-white/40"
            aria-label="Link pessoal de recomendação"
          />
          <button
            type="button"
            onClick={handleCopy}
            disabled={!link}
            data-testid="my-recomendar-copy-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-[10px] text-sm font-semibold bg-white text-[#0F1829] hover:bg-[#F5F2EB] transition disabled:opacity-50"
          >
            {copied ? <CheckCircle2 size={16} className="text-[#10B981]" /> : <Copy size={16} />}
            <span className="hidden sm:inline">{copied ? "Copiado" : "Copiar"}</span>
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="my-recomendar-share-whatsapp"
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold bg-[#25D366] text-white hover:bg-[#1da851] transition ${!link ? "pointer-events-none opacity-50" : ""}`}
          >
            <MessageCircle size={15} /> WhatsApp
          </a>
          <a
            href={mailHref}
            data-testid="my-recomendar-share-email"
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold bg-white text-[#0F1829] hover:bg-[#F5F2EB] transition ${!link ? "pointer-events-none opacity-50" : ""}`}
          >
            <Mail size={15} /> Email
          </a>
          {typeof navigator !== "undefined" && "share" in navigator && (
            <button
              type="button"
              onClick={handleNativeShare}
              disabled={!link}
              data-testid="my-recomendar-share-native"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold bg-white/10 border border-white/20 text-white hover:bg-white/15 transition disabled:opacity-50"
            >
              <Share2 size={15} /> Mais opções
            </button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          icon={Users}
          label="Indicados"
          value={data ? fmtNum(data.total_indicados) : "—"}
          hint="Clientes recomendados directos"
          testid="my-recomendar-kpi-indicados"
        />
        <KpiCard
          icon={ShoppingBag}
          label="Pagas (Total)"
          value={data ? fmtNum(data.encomendas_pagas_total) : "—"}
          hint={data ? `${data.indicados_com_compra} indicados com compra` : "—"}
          testid="my-recomendar-kpi-pagas-total"
        />
        <KpiCard
          icon={TrendingUp}
          label="Pagas (Mês)"
          value={data ? fmtNum(data.encomendas_pagas_mes) : "—"}
          hint={data ? fmt(data.valor_pago_mes) : "—"}
          highlight
          testid="my-recomendar-kpi-pagas-mes"
        />
        <KpiCard
          icon={CircleDollarSign}
          label="Comissão (Mês)"
          value={data ? fmt(data.comissao_mes) : "—"}
          hint="Mês corrente"
          testid="my-recomendar-kpi-comissao-mes"
        />
      </div>

      {/* QR + dicas */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E6E1D6] rounded-[20px] p-6" data-testid="my-recomendar-qr-card">
          <div className="flex items-center gap-2 mb-2">
            <QrCode size={14} className="text-[#978D78]" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#978D78]">QR Code</span>
          </div>
          <h2 className="text-lg text-[#0F1829] mb-3" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            Para partilhas offline
          </h2>
          <p className="text-sm text-[#4B5563] mb-4 leading-relaxed">
            Use este QR code em cartões pessoais, materiais impressos ou em apresentações.
            Quem ler é direccionado para o site com o seu sponsor já pré-associado.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            {qrUrl ? (
              <img
                src={qrUrl}
                alt="QR Code para o seu link de recomendação"
                data-testid="my-recomendar-qr-img"
                className="w-40 h-40 rounded-lg border border-[#E6E1D6] bg-white"
              />
            ) : (
              <div className="w-40 h-40 rounded-lg border border-dashed border-[#E6E1D6] grid place-items-center text-[#978D78]">
                <Loader2 size={24} className="animate-spin" />
              </div>
            )}
            <div className="flex-1 space-y-2">
              <a
                href={qrUrl || "#"}
                download={qrUrl ? `ihtp-qr-${data?.ID_Cliente || ""}.png` : undefined}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="my-recomendar-qr-download"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-[#0F1829] text-white hover:bg-[#1a2542] transition ${!qrUrl ? "pointer-events-none opacity-50" : ""}`}
              >
                <Download size={14} /> Descarregar PNG
              </a>
              <p className="text-xs text-[#6B7280] leading-relaxed">
                Imagem 320×320px gerada a partir do seu link. Renove sempre que mudar de número de afiliado.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#FAF8F3] border border-[#E6E1D6] rounded-[20px] p-6" data-testid="my-recomendar-tips">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={14} className="text-[#978D78]" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#978D78]">Dicas de partilha</span>
          </div>
          <h2 className="text-lg text-[#0F1829] mb-4" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>
            Como tirar mais partido do seu link
          </h2>
          <ul className="space-y-3 text-sm text-[#4B5563] leading-relaxed">
            <li className="flex gap-2">
              <span className="text-[#10B981] font-bold">·</span>
              <span><strong>Personalize</strong> a mensagem antes de enviar — diga porque o IHTP fez a diferença para si.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-[#10B981] font-bold">·</span>
              <span>Use o <strong>QR code</strong> em conversas presenciais — é mais natural do que pedir o nº de telemóvel.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-[#10B981] font-bold">·</span>
              <span>Envie o link <strong>uma vez por mês</strong> nas suas redes — sem spam, com conteúdo relevante.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-[#10B981] font-bold">·</span>
              <span>Acompanhe os ganhos em <a href="/my/comissoes" className="text-[#0F1829] underline">Comissões</a> assim que os indicados pagam.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, hint, highlight, testid }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-[20px] p-5 border ${highlight ? "bg-gradient-to-br from-[#ECFDF5] to-white border-[#A7F3D0]" : "bg-white border-[#E6E1D6]"}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={highlight ? "text-[#065F46]" : "text-[#978D78]"} />
        <div className={`text-[10px] uppercase tracking-[0.2em] ${highlight ? "text-[#065F46]" : "text-[#978D78]"}`}>
          {label}
        </div>
      </div>
      <div
        className={`text-3xl ${highlight ? "text-[#065F46]" : "text-[#0F1829]"}`}
        style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}
      >
        {value}
      </div>
      {hint && <div className="text-[11px] text-[#6B7280] mt-1">{hint}</div>}
    </div>
  );
}
